import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FiChevronRight, FiChevronDown, FiLayers } from 'react-icons/fi';
import { loadTable, getTableData, addRow, saveRow, removeRow } from '../db-viewer/data/dataAccess';
import './PlanServiceMatrix.css';

// =============================================
// Plan ↔ Services — visual matrix (inspired by Manage Folders).
// Services form a tree (parent_service_id); each plan is a clickable "access" chip
// on every service. Click cycles none → read → limited → full and persists to
// site_service_plan_access. Makes the services↔plans relationships visible at a glance.
// =============================================

interface ServiceRow { service_id: number; service_name: string; parent_service_id: number | null; is_deleted?: number; }
interface PlanRow { plan_id: number; plan_name: string; monthly_price: number; is_default?: number; is_deleted?: number; }
interface AccessRow { plan_access_id: number; plan_id: number; service_id: number; access_level: string; is_deleted?: number; }

const LEVELS = ['none', 'read', 'limited', 'full'] as const;
type Level = typeof LEVELS[number];
const LEVEL_LABEL: Record<Level, string> = { none: '—', read: 'Read', limited: 'Limited', full: 'Full' };

const PlanServiceMatrix: React.FC = () => {
  const [services, setServices] = useState<ServiceRow[]>(() => getTableData('site_services') as ServiceRow[]);
  const [plans, setPlans] = useState<PlanRow[]>(() => getTableData('site_service_plans') as PlanRow[]);
  const [access, setAccess] = useState<AccessRow[]>(() => getTableData('site_service_plan_access') as AccessRow[]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  useEffect(() => {
    Promise.all([
      loadTable('site_services'),
      loadTable('site_service_plans'),
      loadTable('site_service_plan_access'),
    ]).then(([s, p, a]) => {
      setServices(s as ServiceRow[]);
      setPlans(p as PlanRow[]);
      setAccess(a as AccessRow[]);
    }).catch(e => setError(e?.message || 'Failed to load'));
  }, []);

  const activePlans = useMemo(
    () => plans.filter(p => p.is_deleted != 1).sort((a, b) => a.plan_id - b.plan_id), // eslint-disable-line eqeqeq
    [plans],
  );
  const topLevel = useMemo(
    () => services.filter(s => s.is_deleted != 1 && (s.parent_service_id == null)).sort((a, b) => a.service_id - b.service_id), // eslint-disable-line eqeqeq
    [services],
  );
  const childrenOf = useCallback(
    (id: number) => services.filter(s => s.is_deleted != 1 && s.parent_service_id == id).sort((a, b) => a.service_id - b.service_id), // eslint-disable-line eqeqeq
    [services],
  );

  const accessRow = useCallback(
    (planId: number, serviceId: number) => access.find(r =>
      r.is_deleted != 1 && r.plan_id == planId && r.service_id == serviceId), // eslint-disable-line eqeqeq
    [access],
  );
  const levelOf = (planId: number, serviceId: number): Level =>
    (accessRow(planId, serviceId)?.access_level as Level) || 'none';

  const toggle = useCallback((id: number) => {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const cycle = useCallback(async (planId: number, serviceId: number) => {
    const key = `${planId}:${serviceId}`;
    const row = accessRow(planId, serviceId);
    const current: Level = (row?.access_level as Level) || 'none';
    const next = LEVELS[(LEVELS.indexOf(current) + 1) % LEVELS.length];
    setBusyKey(key); setError('');
    try {
      if (next === 'none') {
        if (row) {
          await removeRow('site_service_plan_access', row.plan_access_id, 'plan_access_id');
          setAccess(a => a.filter(r => r.plan_access_id !== row.plan_access_id));
        }
      } else if (row) {
        await saveRow('site_service_plan_access', row.plan_access_id, { access_level: next }, 'plan_access_id');
        setAccess(a => a.map(r => r.plan_access_id === row.plan_access_id ? { ...r, access_level: next } : r));
      } else {
        const created = await addRow('site_service_plan_access', {
          plan_id: planId, service_id: serviceId, access_level: next, is_active: 1, is_deleted: 0,
        }, 'plan_access_id') as AccessRow;
        setAccess(a => [...a, created]);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not save change');
    } finally {
      setBusyKey('');
    }
  }, [accessRow]);

  const renderRow = (svc: ServiceRow, depth: number) => {
    const kids = childrenOf(svc.service_id);
    const isParent = kids.length > 0;
    const isOpen = expanded.has(svc.service_id);
    return (
      <React.Fragment key={svc.service_id}>
        <div className="psm-row">
          <div className="psm-svc" style={{ paddingLeft: 8 + depth * 22 }}>
            {isParent ? (
              <button className="psm-expand" onClick={() => toggle(svc.service_id)} title={isOpen ? 'Collapse' : 'Expand'}>
                {isOpen ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}
              </button>
            ) : <span className="psm-expand-spacer" />}
            <span className="psm-svc-name">{svc.service_name}</span>
            {isParent && <span className="psm-svc-count">{kids.length}</span>}
          </div>
          {activePlans.map(p => {
            const lvl = levelOf(p.plan_id, svc.service_id);
            const key = `${p.plan_id}:${svc.service_id}`;
            return (
              <button
                key={p.plan_id}
                className={`psm-chip psm-lvl-${lvl} ${busyKey === key ? 'psm-busy' : ''}`}
                onClick={() => cycle(p.plan_id, svc.service_id)}
                title={`${p.plan_name} → ${svc.service_name}: ${LEVEL_LABEL[lvl]} (click to change)`}
              >
                {LEVEL_LABEL[lvl]}
              </button>
            );
          })}
        </div>
        {isParent && isOpen && kids.map(k => renderRow(k, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="psm">
      <div className="psm-legend">
        <span className="psm-legend-title"><FiLayers size={14} /> Access level (click a cell to cycle):</span>
        {LEVELS.map(l => <span key={l} className={`psm-legend-chip psm-lvl-${l}`}>{LEVEL_LABEL[l]}</span>)}
      </div>
      {error && <div className="psm-error">{error}</div>}

      <div className="psm-table">
        <div className="psm-row psm-head-row">
          <div className="psm-svc psm-head psm-head-svc">Service</div>
          {activePlans.map(p => (
            <div key={p.plan_id} className="psm-head psm-head-plan">
              <span className="psm-head-plan-name">{p.plan_name}</span>
              <span className="psm-head-plan-price">{Number(p.monthly_price) === 0 ? 'Free' : `$${Number(p.monthly_price).toFixed(2)}/mo`}</span>
            </div>
          ))}
        </div>
        {topLevel.map(s => renderRow(s, 0))}
      </div>
    </div>
  );
};

export default PlanServiceMatrix;
